import { CalendarDays, Repeat, Star } from 'lucide-react'
import { useEquipmentScope } from '../equipmentScope'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { formatOccurrenceTime, type PlannerOccurrence } from '@/lib/equipmentOccurrences'
import { REPEAT_CYCLE_LABELS, type RepeatCycle } from '@/api/equipmentApi'
import { eventChipStyle, eventDotStyle } from '@/types/equipment'

export function formatOccurrenceDate(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className='space-y-0.5'>
      <p className='text-[11px] font-medium uppercase tracking-wide text-gray-400'>{label}</p>
      <p className='text-sm text-gray-800'>{value}</p>
    </div>
  )
}

/** Detail card for a single calendar occurrence. */
export function OccurrenceDetailsDialog({
  occurrence,
  units,
  onClose,
}: {
  occurrence: PlannerOccurrence | null
  /** Unit count of the occurrence's equipment type ("applies to all N units"). */
  units?: number
  onClose: () => void
}) {
  const { copy } = useEquipmentScope()
  return (
    <Dialog open={occurrence !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-md overflow-hidden p-0'>
        {occurrence && (
          <>
            {/* Color header */}
            <div
              className='flex items-start gap-3 px-6 py-4'
              style={eventChipStyle(occurrence.color)}
            >
              <span className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20'>
                {occurrence.nonRoutine ? <Star className='h-4 w-4' /> : <Repeat className='h-4 w-4' />}
              </span>
              <div className='min-w-0'>
                <h2 className='text-base font-semibold leading-snug'>{occurrence.label}</h2>
                <p className='mt-0.5 text-xs text-white/80'>
                  {occurrence.eventId
                    ? 'Repeating task'
                    : occurrence.nonRoutine
                      ? 'Calendar-based (non-routine) event'
                      : 'Recurring scheduled event'}
                </p>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4 px-6 py-4'>
              <Field label='Date' value={formatOccurrenceDate(occurrence.date)} />
              <Field
                label='Time'
                value={formatOccurrenceTime(occurrence) || 'All day'}
              />
              <Field label={copy.type} value={occurrence.equipmentType} />
              <Field label='Group' value={occurrence.group} />
              {occurrence.equipmentLabel && (
                <Field label={copy.unit} value={occurrence.equipmentLabel} />
              )}
              {occurrence.repeatCycle && (
                <Field
                  label='Repeats'
                  value={
                    REPEAT_CYCLE_LABELS[occurrence.repeatCycle as RepeatCycle] ??
                    occurrence.repeatCycle
                  }
                />
              )}
              {occurrence.note && <Field label='Cadence' value={occurrence.note} />}
            </div>

            {/* A repeating task is ONE existing work order painted here, so it
                must not carry the per-type "applies to all N units" line, and
                the reader has to be told why Generate Tasks will never create
                anything from these dates. */}
            {occurrence.eventId ? (
              <p className='mx-6 mb-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900'>
                One task on this unit, repeating from its early start date to its due date. It
                already exists in Tasks — Generate Tasks does not create anything from these dates.
              </p>
            ) : (
              units !== undefined &&
              units > 0 && (
                <p className='mx-6 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800'>
                  Applies to all {units} {units === 1 ? copy.unitOne : copy.unitMany} of this {copy.unitLower} type
                </p>
              )
            )}

            <DialogFooter className='px-6 pb-5'>
              <Button onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** All events on a clicked day, grouped into routine / non-routine. */
export function DayEventsDialog({
  date,
  occurrences,
  onClose,
  onSelect,
}: {
  date: string | null
  occurrences: PlannerOccurrence[]
  onClose: () => void
  onSelect: (occ: PlannerOccurrence) => void
}) {
  const { copy } = useEquipmentScope()
  const routine = occurrences.filter((o) => !o.nonRoutine)
  const nonRoutine = occurrences.filter((o) => o.nonRoutine)

  const Section = ({ title, items }: { title: string; items: PlannerOccurrence[] }) =>
    items.length === 0 ? null : (
      <div>
        <p className='mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>{title}</p>
        <div className='space-y-1'>
          {items.map((occ) => {
            const time = formatOccurrenceTime(occ)
            return (
              <button
                key={occ.id}
                type='button'
                onClick={() => onSelect(occ)}
                className='flex w-full items-center gap-2.5 rounded-lg border border-gray-100 px-3 py-2 text-left text-sm transition-colors hover:border-gray-200 hover:bg-gray-50'
              >
                <span
                  className='h-2.5 w-2.5 shrink-0 rounded-full'
                  style={eventDotStyle(occ.color)}
                />
                <span className='min-w-0 flex-1 truncate text-gray-800'>
                  {occ.equipmentType} — {occ.label}
                </span>
                {time && (
                  <span className='shrink-0 text-xs font-medium text-gray-500'>{time}</span>
                )}
                {occ.nonRoutine && <Star className='h-3.5 w-3.5 shrink-0 text-pink-500' />}
              </button>
            )
          })}
        </div>
      </div>
    )

  return (
    <Dialog open={date !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-md'>
        {date && (
          <>
            <div className='flex items-start gap-3'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
                <CalendarDays className='h-5 w-5' />
              </div>
              <div>
                <h2 className='text-base font-semibold text-gray-900'>{formatOccurrenceDate(date)}</h2>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  {occurrences.length} {copy.eventNoun} {occurrences.length === 1 ? 'event' : 'events'} on this day
                </p>
              </div>
            </div>

            <div className='mt-4 max-h-[50vh] space-y-4 overflow-y-auto pr-1'>
              {occurrences.length === 0 ? (
                <p className='py-6 text-center text-sm text-gray-400'>No {copy.eventNoun} events on this day.</p>
              ) : (
                <>
                  <Section title='Scheduled (recurring)' items={routine} />
                  <Section title='Calendar-based (non-routine)' items={nonRoutine} />
                </>
              )}
            </div>

            <DialogFooter className='mt-4'>
              <Button variant='outline' onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
